/**
 * docSourceAttachments — the canonical-source sibling is never a chip.
 *
 * A `.source.md` delivered beside a pdf/docx is the markdown a later edit
 * regenerates the document from. It has exactly ONE consumer, the server-side
 * document registry, which records its storage path. To a person it is a chip
 * they cannot use sitting next to the real document.
 *
 * WHY A CLIENT-SIDE FILTER EXISTS AT ALL (QA Series C, Test 16 retest,
 * 2026-09-07). The server now strips it at the persist chokepoint
 * (`turnPayloadSlim`), which fixes every turn from here on. It does NOT fix
 * the messages ALREADY WRITTEN: a thread that received documents before that
 * landed still has the sibling in its message documents, and would render the
 * dead chip for as long as the thread exists. A migration over every account's
 * history to delete two rows per delivered document is a far bigger and more
 * dangerous change than declining to draw them.
 *
 * So: the server stops producing it, and the read boundary stops showing what
 * was already produced. Both halves are needed, and neither is a substitute
 * for the other.
 */

/** How the registry stamps the sibling when the marker survives the seam. */
export const DOC_SOURCE_GENERATED_BY = 'doc-source';

/**
 * Is this attachment a canonical-source sibling?
 *
 * Marker OR name suffix: seams that lose `generatedBy` still classify right,
 * which is the same both-ways test the server's own predicate uses.
 */
export function isDocSourceAttachment(att: unknown): boolean {
  const a = att as { name?: unknown; generatedBy?: unknown } | null;
  if (!a || typeof a !== 'object') return false;
  if (a.generatedBy === DOC_SOURCE_GENERATED_BY) return true;
  return /\.source\.md$/i.test(typeof a.name === 'string' ? a.name : '');
}

/**
 * `list` without the canonical-source siblings.
 *
 * Returns the SAME array reference when there is nothing to remove, so the
 * ordinary message costs no allocation and React sees no changed prop.
 * `undefined` in, `undefined` out — callers pass optional fields straight
 * through.
 */
export function withoutDocSourceAttachments<T>(list: T[] | undefined | null): T[] | undefined {
  if (!Array.isArray(list)) return undefined;
  const kept = list.filter((att) => !isDocSourceAttachment(att));
  return kept.length === list.length ? list : kept;
}
